import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Checkbox } from 'primereact/checkbox';
import { getRequest, postRequest } from '../../api/ApiController';
import { DatabaseContext } from '../../redux/DatabaseStore';
import AuthContext from '../../components/AuthContext';
import UtilContext from '../../components/UtilContext';
import { LocaleContext } from '../../components/LocaleContext';
import MyEditor from '../../components/Editor';
import { CRM_COMMUNICATION_HUB_TYPE_ITEMS } from '../../utils/crmCommunicationTypeIcons';
import { crmLevelTypeAliasesForTicketType } from '../../utils/crmCommunicationTypeAliases';

const ITEMS = CRM_COMMUNICATION_HUB_TYPE_ITEMS;

const EMPTY_FORM = {
  ticket_type: '',
  title: '',
  description: '',
  files: [],
  submitter_same_as_sender: true,
  primary_company_id: null,
  primary_company_contact_id: null,
  recipientCompanies: [],
  recipients: {},
  sender_user_id: null,
  sending_option: 'immediately',
  sending_date: '',
  sending_time: '',
  email: true,
};

function removeDiacritics(str) {
  return String(str || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function isEditorHtmlEmpty(v) {
  const s = String(v || '')
    .replace(/<p><br><\/p>/gi, '')
    .replace(/<br\s*\/?>/gi, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .trim();
  return s === '';
}

function splitIntroText(intro) {
  const text = String(intro || '').trim();
  if (!text) return { main: '', thanks: '' };
  const m = text.match(/^(.*?)(\s*(Ďakujeme!|Thank you!|Danke!))$/u);
  if (!m) return { main: text, thanks: '' };
  return { main: (m[1] || '').trim(), thanks: (m[3] || '').trim() };
}

export default function CrmCommunicationNewTaskHub() {
  const { t } = useContext(LocaleContext);
  const [state] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { toast, setDialogText, setVisible } = useContext(UtilContext);
  const [activeIndex, setActiveIndex] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [companiesLoadForbidden, setCompaniesLoadForbidden] = useState(false);
  const [contactsByCompany, setContactsByCompany] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const companiesRes = await getRequest('/api/crm_communication_catalog/companies_for_ticket');
        if (cancelled) return;
        if (companiesRes?.status === 403) {
          setCompanies([]);
          setCompaniesLoadForbidden(true);
          toast?.current?.show({ severity: 'error', summary: t('CrmCommunication.Form.CompaniesLoadForbidden'), life: 3500 });
        } else if (companiesRes?.status && companiesRes.status !== 200) {
          setCompanies([]);
          setCompaniesLoadForbidden(false);
          toast?.current?.show({ severity: 'error', summary: t('CrmCommunication.Form.CompaniesLoadFailed'), life: 3000 });
        } else {
          setCompaniesLoadForbidden(false);
          setCompanies(Array.isArray(companiesRes?.data) ? companiesRes.data : []);
        }
      } catch (_) {
        if (!cancelled) {
          setCompanies([]);
          setCompaniesLoadForbidden(false);
          toast?.current?.show({ severity: 'error', summary: t('CrmCommunication.Form.CompaniesLoadFailed'), life: 3000 });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchContactsForCompany = useCallback(async (companyId) => {
    if (!companyId || contactsByCompany[companyId]) return;
    try {
      const res = await getRequest(`/api/crm_communication_catalog/company_contacts?company_id=${companyId}`);
      const rows = Array.isArray(res?.data) ? res.data : [];
      setContactsByCompany((prev) => ({ ...prev, [companyId]: rows }));
    } catch (_) {
      setContactsByCompany((prev) => ({ ...prev, [companyId]: [] }));
    }
  }, [contactsByCompany]);

  const formik = useFormik({
    initialValues: { ...EMPTY_FORM, sender_user_id: user?.id ?? null },
    onSubmit: async (values) => {
      if (activeIndex == null || !values.ticket_type) return;
      if (!values.title.trim()) {
        toast?.current?.show({ severity: 'warn', summary: t('CrmCommunication.Form.ValidationTitle'), life: 2200 });
        return;
      }
      if (isEditorHtmlEmpty(values.description)) {
        toast?.current?.show({ severity: 'warn', summary: t('CrmCommunication.Form.ValidationBody'), life: 2200 });
        return;
      }
      if (values.ticket_type === 'announcement' && (!Array.isArray(values.recipientCompanies) || values.recipientCompanies.length === 0)) {
        toast?.current?.show({ severity: 'warn', summary: t('CrmCommunication.Form.ValidationRecipients'), life: 2200 });
        return;
      }
      if (values.ticket_type === 'announcement' && values.sending_option === 'once' && (!values.sending_date || !values.sending_time)) {
        toast?.current?.show({ severity: 'warn', summary: `${t('ServiceTaskDetail.SetDateTime')} (${t('Common.RequiredField')})`, life: 2200 });
        return;
      }
      if ((values.ticket_type === 'acquisition' || values.ticket_type === 'quote_request') && !values.primary_company_id) {
        toast?.current?.show({ severity: 'warn', summary: t('CrmCommunication.Form.ValidationPrimaryCustomer'), life: 2200 });
        return;
      }
      setSaving(true);
      setDialogText?.(t('Common.Saving'));
      setVisible?.(true);
      try {
        const formData = new FormData();
        formData.append('type', values.ticket_type);
        formData.append('title', values.title.trim());
        formData.append('body_html', values.description || '');
        formData.append('submitter_same_as_sender', values.submitter_same_as_sender ? '1' : '0');
        const fileList = Array.isArray(values.files) ? values.files : [];
        for (let i = 0; i < fileList.length; i += 1) {
          if (fileList[i]) formData.append('files[]', fileList[i]);
        }
        if (values.ticket_type === 'announcement') {
          formData.append('email', '1');
          const uniqueCompanyIds = [...new Set(values.recipientCompanies.map((x) => Number(x)).filter((x) => Number.isFinite(x) && x > 0))];
          const recipients = uniqueCompanyIds.map((cid) => {
            const contactId = values.recipients?.[cid] ?? null;
            if (contactId == null) return { company_id: cid };
            return { company_id: cid, company_contact_id: contactId };
          });
          formData.append('recipients', JSON.stringify(recipients));
          if (values.sending_option === 'once' && values.sending_date && values.sending_time) {
            formData.append('scheduled_send_at', new Date(`${values.sending_date}T${values.sending_time}`).toISOString());
          }
        } else {
          if (values.primary_company_id) formData.append('primary_company_id', String(values.primary_company_id));
          if (values.primary_company_contact_id) formData.append('primary_company_contact_id', String(values.primary_company_contact_id));
        }
        const res = await postRequest('/api/crm_communication_tickets', formData);
        if (res?.status === 200 && res?.data?.id) {
          toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), life: 3000, detail: t('ServiceTask.RecordSaved') });
          formik.resetForm({ values: { ...EMPTY_FORM, sender_user_id: user?.id ?? null } });
          setActiveIndex(null);
        } else {
          throw new Error(String(res?.message || JSON.stringify(res?.errors || res)));
        }
      } catch (e) {
        toast?.current?.show({ severity: 'error', summary: t('CrmCommunication.Toast.Error'), detail: String(e?.message || e), life: 4000 });
      } finally {
        setSaving(false);
        setVisible?.(false);
      }
    },
  });

  const companyOptions = useMemo(
    () => (companies || []).map((c) => ({ label: c.name || `ID ${c.id}`, value: c.id, searchLabel: removeDiacritics(c.name).toLowerCase() })),
    [companies],
  );

  const checkIndexPermission = useCallback((index) => {
    const row = ITEMS.find((x) => x.index === index);
    if (!row) return false;
    if (user?.is_superuser) return true;
    const roles = Array.isArray(user?.roles) ? user.roles : [];
    const isAdmin = roles.some((r) => (typeof r === 'string' ? r === 'admin' : String(r?.name || '').toLowerCase() === 'admin'));
    if (isAdmin) return true;
    const typeAliases = crmLevelTypeAliasesForTicketType(row.ticketType);
    const levels = (state.crm_levels || []).filter((l) => l?.task === 'creation' && typeAliases.includes(String(l?.type || '')));
    if (levels.length === 0) return true;
    const levelIds = levels.map((l) => Number(l.id));
    const settings = (state.crm_level_user_settings || []).filter((s) => levelIds.includes(Number(s.crm_level_id)));
    if (settings.length === 0) return true;
    return settings.some((s) => {
      if (s?.user_id != null) return Number(s.user_id) === Number(user?.id);
      if (s?.department_id != null) return Number(s.department_id) === Number(user?.employee?.department_id);
      return false;
    });
  }, [state.crm_level_user_settings, state.crm_levels, user?.employee?.department_id, user?.id, user?.is_superuser, user?.roles]);

  const onTabChange = useCallback((e) => {
    const idx = typeof e.index === 'number' ? e.index : null;
    setActiveIndex(idx);
    formik.resetForm({ values: { ...EMPTY_FORM, sender_user_id: user?.id ?? null } });
    if (idx == null) return;
    const row = ITEMS.find((x) => x.index === idx);
    if (!row) return;
    formik.setFieldValue('ticket_type', row.ticketType);
  }, [formik, user?.id]);

  const primaryContacts = formik.values.primary_company_id ? (contactsByCompany[formik.values.primary_company_id] || []) : [];
  const senderOptions = useMemo(() => {
    const rows = Array.isArray(state.users) && state.users.length > 0
      ? state.users
      : (user ? [user] : []);
    return rows.map((u) => ({
      label: u?.employee?.name || u?.username || `ID ${u.id}`,
      value: u.id,
      searchLabel: removeDiacritics(u?.employee?.name || u?.username || '').toLowerCase(),
    }));
  }, [state.users, user]);
  const attachmentCount = Array.isArray(formik.values.files) ? formik.values.files.length : 0;
  const controlClass = 'w-full px-4 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40';
  const nativeInputClass = 'block w-full px-4 py-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40';

  return (
    <form onSubmit={formik.handleSubmit} className="flex h-full min-h-0 flex-col">
      <header className="w-full border rounded-sm bg-[#333] px-4 py-4 text-white shrink-0">
        <h1 className="text-left text-3xl font-semibold">{t('Menu.CRM.Communication.NewTask')}</h1>
      </header>
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="card flex flex-col rounded-sm border px-4 py-4 shadow-xl">
          <Accordion activeIndex={activeIndex} onTabChange={onTabChange}>
            {ITEMS.map((row) => {
              const Icon = row.icon;
              const disabled = !checkIndexPermission(row.index);
              const isAnnouncement = row.ticketType === 'announcement';
              const isSelected = activeIndex === row.index;
              const introParts = splitIntroText(t(row.introKey));
              return (
                <AccordionTab
                  key={row.ticketType}
                  disabled={disabled}
                  className={`${(activeIndex !== null && activeIndex !== row.index) ? 'hidden' : ''}`}
                  header={(
                    <React.Fragment>
                      <span className="inline-flex items-center" title={t(row.tabNameKey)}>
                        <Icon width={10} height={10} />
                      </span>
                      <span>{t(row.tabNameKey)}</span>
                    </React.Fragment>
                  )}
                >
                  <p className="text-sm text-gray-700">{introParts.main}</p>
                  {introParts.thanks ? <p className="mb-2 text-sm text-gray-700">{introParts.thanks}</p> : null}
                  <div className={`${!isSelected ? 'hidden' : ''}`}>
                    <div className="mb-2">
                      <label className="block font-semibold text-md">{t(`CrmCommunication.Form.FieldTitle.${row.ticketType}`)}</label>
                      <input
                        name="title"
                        required
                        value={formik.values.title}
                        onChange={formik.handleChange}
                        className={nativeInputClass}
                      />
                    </div>
                    <div className="mb-2">
                      <label className="block font-semibold text-md">{t(`CrmCommunication.Form.FieldBody.${row.ticketType}`)}</label>
                      <MyEditor editorValue={formik.values.description} fileAccept="*" formik={formik} />
                      {t('ServiceTaskAdd.AttachmentCount')}: {attachmentCount}
                    </div>
                    {isAnnouncement ? (
                      <>
                        <div className="mb-2">
                          <label className="block font-semibold text-md">{t('CrmCommunication.Form.Field.Recipients')}</label>
                          {companiesLoadForbidden ? (
                            <p className="mb-2 text-sm text-amber-700">{t('CrmCommunication.Form.CompaniesLoadForbidden')}</p>
                          ) : companyOptions.length === 0 ? (
                            <p className="mb-2 text-sm text-amber-700">{t('CrmCommunication.Form.NoCompaniesForTrader')}</p>
                          ) : null}
                          <MultiSelect
                            value={formik.values.recipientCompanies}
                            options={companyOptions}
                            optionLabel="label"
                            optionValue="value"
                            filter
                            filterBy="searchLabel"
                            filterMatchMode="contains"
                            filterPlaceholder={t('Common.Search')}
                            placeholder={t('CrmCommunication.Form.RecipientsPlaceholder')}
                            display="chip"
                            className={controlClass}
                            onChange={async (e) => {
                              const selected = Array.isArray(e.value) ? e.value : [];
                              formik.setFieldValue('recipientCompanies', selected);
                              const nextRecipients = {};
                              selected.forEach((cid) => {
                                nextRecipients[cid] = formik.values.recipients?.[cid] ?? null;
                              });
                              formik.setFieldValue('recipients', nextRecipients);
                              await Promise.all(selected.map((cid) => fetchContactsForCompany(cid)));
                            }}
                          />
                        </div>
                        {formik.values.recipientCompanies.map((cid) => (
                          <div className="mb-2" key={`recipient-${cid}`}>
                            <label className="block text-sm font-medium text-gray-700">
                              {t('CrmCommunication.Form.Field.Buyer')}: {(companyOptions.find((x) => x.value === cid)?.label || `ID ${cid}`)}
                            </label>
                            <Dropdown
                              value={formik.values.recipients?.[cid] ?? null}
                              options={(contactsByCompany[cid] || []).map((ct) => ({ label: ct.name || `ID ${ct.id}`, value: ct.id, searchLabel: removeDiacritics(ct.name).toLowerCase() }))}
                              onChange={(e) => {
                                formik.setFieldValue('recipients', { ...formik.values.recipients, [cid]: e.value ?? null });
                              }}
                              filter
                              filterBy="searchLabel"
                              filterMatchMode="contains"
                              filterPlaceholder={t('Common.Search')}
                              showClear
                              placeholder={t('CrmCommunication.Form.BuyerPlaceholder')}
                              className={controlClass}
                            />
                          </div>
                        ))}
                        <div className="mb-2">
                          <label className="block font-semibold text-md">{t('ServiceTaskDetail.SendingTimeSetup')}</label>
                          <select
                            name="sending_option"
                            value={formik.values.sending_option}
                            onChange={(e) => formik.setFieldValue('sending_option', e.target.value)}
                            className={nativeInputClass}
                          >
                            <option value="immediately">{t('ServiceTaskDetail.AfterApproval')}</option>
                            <option value="once">{t('ServiceTaskDetail.SetDateTime')}</option>
                          </select>
                        </div>
                        {formik.values.sending_option === 'once' ? (
                          <div className="mb-2">
                            <label className="block font-semibold text-sm">{t('ServiceTaskDetail.SendingDate')}</label>
                            <input
                              type="date"
                              name="sending_date"
                              value={formik.values.sending_date}
                              onChange={formik.handleChange}
                              className={nativeInputClass}
                            />
                            <label className="mt-2 block text-md font-semibold text-gray-800">{t('ServiceTaskDetail.SendingTime')}</label>
                            <input
                              type="time"
                              name="sending_time"
                              value={formik.values.sending_time}
                              onChange={formik.handleChange}
                              className={nativeInputClass}
                            />
                          </div>
                        ) : null}
                      </>
                    ) : (
                      <>
                        <div className="mb-2">
                          <label className="block font-semibold text-md">{t('CrmCommunication.Form.Field.PrimaryCustomer')}</label>
                          {companiesLoadForbidden ? (
                            <p className="mb-2 text-sm text-amber-700">{t('CrmCommunication.Form.CompaniesLoadForbidden')}</p>
                          ) : companyOptions.length === 0 ? (
                            <p className="mb-2 text-sm text-amber-700">{t('CrmCommunication.Form.NoCompaniesForTrader')}</p>
                          ) : null}
                          <Dropdown
                            value={formik.values.primary_company_id}
                            options={companyOptions}
                            onChange={async (e) => {
                              const cid = e.value ?? null;
                              formik.setFieldValue('primary_company_id', cid);
                              formik.setFieldValue('primary_company_contact_id', null);
                              if (cid) await fetchContactsForCompany(cid);
                            }}
                            filter
                            filterBy="searchLabel"
                            filterMatchMode="contains"
                            filterPlaceholder={t('Common.Search')}
                            showClear
                            placeholder={t('CrmCommunication.Form.RecipientsPlaceholder')}
                            className={controlClass}
                          />
                        </div>
                        <div className="mb-2">
                          <label className="block font-semibold text-md">{t('CrmCommunication.Form.Field.Buyer')}</label>
                          <Dropdown
                            value={formik.values.primary_company_contact_id}
                            options={primaryContacts.map((ct) => ({ label: ct.name || `ID ${ct.id}`, value: ct.id, searchLabel: removeDiacritics(ct.name).toLowerCase() }))}
                            onChange={(e) => formik.setFieldValue('primary_company_contact_id', e.value ?? null)}
                            filter
                            filterBy="searchLabel"
                            filterMatchMode="contains"
                            filterPlaceholder={t('Common.Search')}
                            showClear
                            placeholder={t('CrmCommunication.Form.BuyerPlaceholder')}
                            className={controlClass}
                          />
                        </div>
                      </>
                    )}
                    <div className="mb-2 flex items-center gap-2">
                      <Checkbox inputId={`submitterSame-${row.ticketType}`} checked={!!formik.values.submitter_same_as_sender} onChange={(e) => formik.setFieldValue('submitter_same_as_sender', !!e.checked)} />
                      <label htmlFor={`submitterSame-${row.ticketType}`} className="text-sm">{t('CrmCommunication.Form.SubmitterSame')}</label>
                    </div>
                    {!formik.values.submitter_same_as_sender ? (
                      <div className="mb-2">
                        <label className="block font-semibold text-md">{t('CrmCommunication.Form.Field.Sender')}</label>
                        <Dropdown
                          value={formik.values.sender_user_id}
                          options={senderOptions}
                          onChange={(e) => formik.setFieldValue('sender_user_id', e.value ?? null)}
                          filter
                          filterBy="searchLabel"
                          filterMatchMode="contains"
                          filterPlaceholder={t('Common.Search')}
                          placeholder={t('CrmCommunication.Form.Field.Sender')}
                          className={controlClass}
                        />
                      </div>
                    ) : null}
                    {isAnnouncement ? (
                      <div className="mb-2 flex items-center gap-6">
                        <div className="flex items-center gap-2">
                          <Checkbox inputId={`email-${row.ticketType}`} checked onChange={() => {}} className="p-button-sm p-button-text" />
                          <label htmlFor={`email-${row.ticketType}`} className="text-sm">{t('Common.Email')}</label>
                        </div>
                      </div>
                    ) : null}
                    <button
                      type="submit"
                      disabled={saving}
                      className="flex justify-center items-center mt-4 w-full h-10 font-medium mb-2 px-2 py-1 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600 disabled:opacity-25"
                    >
                      {t('Common.Save')}
                    </button>
                  </div>
                </AccordionTab>
              );
            })}
          </Accordion>
        </div>
      </div>
    </form>
  );
}
